import dayjs from 'dayjs';
import { LuhnChecksum } from '@/luhnChecksum';

// This implementation is made to match its' backend counterpart (found in
// se.idkollen.webportal.util.SSN).

export enum SSNValidationResult {
  VALID,
  INVALID_FORMAT,
  INVALID_CONTROL_DIGITS,
  INVALID_DATE,
}

type Country = 'SWEDEN' | 'NORWAY' | 'DENMARK' | 'FINLAND';

// eslint-disable-next-line prettier/prettier
const COUNTRIES: Country[] = ['SWEDEN', 'NORWAY', 'DENMARK', 'FINLAND'];
// eslint-disable-next-line prettier/prettier
const FINNISH_CONTROL_DIGITS = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', 'A', 'B', 'C', 'D', 'E', 'F', 'H', 'J', 'K', 'L', 'M', 'N', 'P', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y'];

function validateSwedishSSN(ssn: string): SSNValidationResult {
  const checkFormat = (): boolean => {
    return /^(\d{2})?\d{6}[+-]?\d{4}$/.test(ssn);
  };

  const checkControlDigits = (): boolean => {
    const tenDigitSsn = normalizeSwedishSSN(ssn).substring(2);

    return LuhnChecksum.verify(tenDigitSsn);
  };

  const checkDate = (): boolean => {
    const twelveDigitSsn = normalizeSwedishSSN(ssn);
    const date = twelveDigitSsn.substring(0, 8);

    return dayjs(date, 'YYYYMMDD', true).isValid();
  };

  if (!checkFormat()) {
    return SSNValidationResult.INVALID_FORMAT;
  } else if (!checkControlDigits()) {
    return SSNValidationResult.INVALID_CONTROL_DIGITS;
  } else if (!checkDate()) {
    return SSNValidationResult.INVALID_DATE;
  } else {
    return SSNValidationResult.VALID;
  }
}

function validateNorwegianSSN(ssn: string): SSNValidationResult {
  const checkFormat = (): boolean => {
    return /^\d{11}$/.test(ssn);
  };

  const checkControlDigits = (weights: number[]): boolean => {
    let sum = 0;

    for (let i = 0; i < weights.length; i++) {
      sum += weights[i] * Number(ssn.charAt(i));
    }

    return sum % 11 === 0;
  };

  const checkDate = (): boolean => {
    const date = ssn.substring(0, 6);

    return dayjs(date, 'DDMMYY', true).isValid();
  };

  if (!checkFormat()) {
    return SSNValidationResult.INVALID_FORMAT;
  } else if (
    !checkControlDigits([3, 7, 6, 1, 8, 9, 4, 5, 2, 1]) ||
    !checkControlDigits([5, 4, 3, 2, 7, 6, 5, 4, 3, 2, 1])
  ) {
    return SSNValidationResult.INVALID_CONTROL_DIGITS;
  } else if (!checkDate()) {
    return SSNValidationResult.INVALID_DATE;
  } else {
    return SSNValidationResult.VALID;
  }
}

function validateFinnishSSN(ssn: string): SSNValidationResult {
  const checkFormat = (): boolean => {
    return /^\d{6}[+aA-]\d{3}[\da-zA-Z]$/.test(ssn);
  };

  const checkControlDigit = (): boolean => {
    const digit = Number(ssn.substring(0, 6) + ssn.substring(7, 10));
    const control = FINNISH_CONTROL_DIGITS[digit % 31];

    return ssn.charAt(10) === control;
  };

  const checkDate = (): boolean => {
    const date = ssn.substring(0, 6);

    return dayjs(date, 'DDMMYY', true).isValid();
  };

  if (!checkFormat()) {
    return SSNValidationResult.INVALID_FORMAT;
  } else if (!checkControlDigit()) {
    return SSNValidationResult.INVALID_CONTROL_DIGITS;
  } else if (!checkDate()) {
    return SSNValidationResult.INVALID_DATE;
  } else {
    return SSNValidationResult.VALID;
  }
}

function validateDanishSSN(ssn: string): SSNValidationResult {
  const checkFormat = (): boolean => {
    return /^\d{6}-?\d{4}$/.test(ssn);
  };

  const checkControlDigits = (weights: number[]): boolean => {
    const twelveDigitSsn = normalizeDanishSSN(ssn);
    let sum = 0;

    for (let i = 0; i < weights.length; i++) {
      sum += weights[i] * Number(twelveDigitSsn.charAt(i));
    }

    return sum % 11 === 0;
  };

  const checkDate = (): boolean => {
    const date = ssn.substring(0, 6);

    return dayjs(date, 'DDMMYY', true).isValid();
  };

  if (!checkFormat()) {
    return SSNValidationResult.INVALID_FORMAT;
  } else if (!checkControlDigits([4, 3, 2, 7, 6, 5, 4, 3, 2, 1])) {
    return SSNValidationResult.INVALID_CONTROL_DIGITS;
  } else if (!checkDate()) {
    return SSNValidationResult.INVALID_DATE;
  } else {
    return SSNValidationResult.VALID;
  }
}

function normalizeSwedishSSN(ssn: string): string {
  if (ssn.length < 12) {
    const currentYear = new Date().getFullYear();
    const currentYearPartStart = Math.floor(currentYear / 100);
    const currentYearPartEnd = currentYear % 100;
    let ssnYearPartStart = currentYearPartStart;
    const ssnYearPartEnd = Number(ssn.substring(0, 2));

    if (ssnYearPartEnd >= currentYearPartEnd) {
      ssnYearPartStart -= 1;
    }

    if (ssn.includes('+')) {
      ssnYearPartStart -= 1;
    }

    ssn = `${ssnYearPartStart}${ssn}`;
  }

  return ssn.replace(/[+-]/, '');
}

function normalizeNorwegianSSN(ssn: string): string {
  return ssn;
}

function normalizeFinnishSSN(ssn: string): string {
  return ssn;
}

function normalizeDanishSSN(ssn: string): string {
  return ssn.replace('-', '');
}

export const SSN = {
  /**
   * Checks that the given SSN is valid in the given country, or in any country if no
   * country is provided.
   */
  validate(ssn: string, country?: Country): SSNValidationResult {
    if (!country) {
      for (const country of COUNTRIES) {
        if (this.validate(ssn, country) === SSNValidationResult.VALID) {
          return SSNValidationResult.VALID;
        }
      }

      return SSNValidationResult.INVALID_FORMAT;
    }

    switch (country) {
      case 'SWEDEN':
        return validateSwedishSSN(ssn);
      case 'NORWAY':
        return validateNorwegianSSN(ssn);
      case 'DENMARK':
        return validateDanishSSN(ssn);
      case 'FINLAND':
        return validateFinnishSSN(ssn);
      default:
        return SSNValidationResult.INVALID_FORMAT;
    }
  },

  /**
   * Normalizes the given SSN based on the given country. If no country is provided,
   * then a best-effort normalization is performed based on the countries where it's
   * valid. This may give the wrong result if the SSN if ambiguous, so try to always
   * provide a country.
   *
   * The given SSN is returned as-is if it's not a valid SSN.
   */
  normalize(ssn: string, country?: Country): string {
    if (!country) {
      for (const country of COUNTRIES) {
        if (this.validate(ssn, country) === SSNValidationResult.VALID) {
          return this.normalize(ssn, country);
        }
      }

      return ssn;
    }

    if (this.validate(ssn, country) !== SSNValidationResult.VALID) {
      return ssn;
    }

    switch (country) {
      case 'SWEDEN':
        return normalizeSwedishSSN(ssn);
      case 'NORWAY':
        return normalizeNorwegianSSN(ssn);
      case 'DENMARK':
        return normalizeDanishSSN(ssn);
      case 'FINLAND':
        return normalizeFinnishSSN(ssn);
      default:
        return ssn;
    }
  },
};
